import type { IAdminCompanyRow } from '@commenda/api/admin';
import {
  bulkSendCompanyMessage,
  BulkSendCompanyMessageResultItem,
  bulkSendMessage,
  BulkSendMessageResultItem,
  TBulkSendMessageSender,
} from '@commenda/api/chatRoom';
import {
  extractFailedCompanyResultsFromTasks,
  extractFailedResultsFromTasks,
  getBulkMessageJobStatus,
  TBulkMessageJobStatus,
} from '@commenda/api/job';
import {
  Dialog,
  DialogContent,
  useToggle,
} from '@commenda/kakashi';
import { IServiceRequest } from '@commenda/types/src/service-request/types';
import { useNotification } from '../../hooks/useNotification';
import { usePolling } from '../../hooks/usePolling';
import { useCallback, useEffect, useState } from 'react';
import { ComposeStep } from './ComposeStep';
import { ReviewStep } from './ReviewStep';
import { ProcessingStep } from './ProcessingStep';
import { SuccessStep } from './SuccessStep';
import { FailedStep } from './FailedStep';
import { CompanyReviewStep } from './CompanyReviewStep';
import { CompanyFailedStep } from './CompanyFailedStep';
import { downloadFailedCsv } from './downloadFailedCsv';
import { downloadFailedCompanyCsv } from './downloadFailedCompanyCsv';

type Step = 'compose' | 'review' | 'processing' | 'success' | 'failed';

const POLLING_INTERVAL = 3000;
const POLLING_TIMEOUT = 300000;

export type SendBulkMessageTarget =
  | { kind: 'serviceRequests'; items: IServiceRequest[]; sender?: TBulkSendMessageSender }
  | { kind: 'companies'; items: IAdminCompanyRow[] };

interface SendBulkMessageModalProps {
  isOpen: boolean;
  onClose: () => void;
  target: SendBulkMessageTarget;
  onSuccess: () => void;
}

export const SendBulkMessageModal = ({
  isOpen,
  onClose,
  target,
  onSuccess,
}: SendBulkMessageModalProps) => {
  const { setNotification } = useNotification();
  const [currentStep, setCurrentStep] = useState<Step>('compose');
  const [message, setMessage] = useState('');
  const [confirmText, setConfirmText] = useState('');
  const isSending = useToggle(false);
  const [failedSrResults, setFailedSrResults] = useState<BulkSendMessageResultItem[]>([]);
  const [failedCompanyResults, setFailedCompanyResults] = useState<BulkSendCompanyMessageResultItem[]>([]);

  const [jobId, setJobId] = useState<string | null>(null);
  const [isPollingEnabled, setIsPollingEnabled] = useState(false);
  const [jobStatus, setJobStatus] = useState<TBulkMessageJobStatus | null>(null);

  const selectedCount = target.items.length;
  const processedRows = jobStatus?.job.processedRows ?? 0;
  const totalRows = jobStatus?.job.totalRows ?? selectedCount;
  const progressPercent = totalRows > 0 ? Math.round((processedRows / totalRows) * 100) : 0;

  const handleClose = () => {
    if (currentStep === 'processing') return;
    setCurrentStep('compose');
    setMessage('');
    setConfirmText('');
    setFailedSrResults([]);
    setFailedCompanyResults([]);
    setJobId(null);
    setIsPollingEnabled(false);
    setJobStatus(null);
    onClose();
  };

  const checkJobStatus = useCallback(async () => {
    if (!jobId) return;

    const result = await getBulkMessageJobStatus(jobId);

    if (!result) {
      setIsPollingEnabled(false);
      setCurrentStep('failed');
      setNotification({
        show: true,
        valid: false,
        title: 'Error',
        message: 'Failed to check job status.',
      });
      return;
    }

    setJobStatus(result);

    if (result.isJobCompleted) {
      setIsPollingEnabled(false);

      const failureCount = result.job.failureCount ?? 0;
      const successCount = result.job.successCount ?? 0;

      if (failureCount === 0) {
        setCurrentStep('success');
        setNotification({
          show: true,
          valid: true,
          title: 'Success',
          message: `Successfully sent ${successCount} message(s).`,
        });
        onSuccess();
      } else {
        if (target.kind === 'serviceRequests') {
          const failed = result.tasks ? extractFailedResultsFromTasks(result.tasks) : [];
          setFailedSrResults(failed);
          if (failed.length > 0) {
            downloadFailedCsv(failed, target.items, message);
          }
        } else {
          const failed = result.tasks ? extractFailedCompanyResultsFromTasks(result.tasks) : [];
          setFailedCompanyResults(failed);
          if (failed.length > 0) {
            downloadFailedCompanyCsv(failed, target.items, message);
          }
        }

        setNotification({
          show: true,
          valid: false,
          title: 'Partial Failure',
          message: `${successCount} message(s) sent. ${failureCount} message(s) failed.`,
        });

        setCurrentStep('failed');
      }
    }
  }, [jobId, setNotification, onSuccess, target, message]);

  usePolling(checkJobStatus, {
    enabled: isPollingEnabled,
    interval: POLLING_INTERVAL,
    timeout: POLLING_TIMEOUT,
  });

  useEffect(() => {
    if (currentStep === 'processing' && !isPollingEnabled && jobId) {
      setNotification({
        show: true,
        valid: false,
        title: 'Timeout',
        message: 'Processing is taking longer than expected. The job will continue in the background.',
      });
      setCurrentStep('success');
      onSuccess();
    }
  }, [isPollingEnabled, currentStep, jobId, setNotification, onSuccess]);

  const handleSend = useCallback(async () => {
    isSending.on();
    try {
      const result = target.kind === 'serviceRequests'
        ? await bulkSendMessage({
          serviceRequestIds: target.items.map((sr) => sr.id),
          message,
          sender: target.sender,
        })
        : await bulkSendCompanyMessage({
          companyIds: target.items.map((c) => c.id),
          message,
        });

      if (!result.success) {
        setNotification({
          show: true,
          valid: false,
          title: 'Error',
          message: result.error || 'Failed to start bulk message job.',
        });
        isSending.off();
        return;
      }

      const data = result.data!;
      setJobId(data.job.id);
      setIsPollingEnabled(true);
      setCurrentStep('processing');
    } catch {
      setNotification({
        show: true,
        valid: false,
        title: 'Error',
        message: 'An unexpected error occurred while starting the bulk message job.',
      });
    } finally {
      isSending.off();
    }
  }, [message, target, isSending, setNotification]);

  const isSentAsRequestingUser =
    target.kind === 'serviceRequests' && target.sender === 'REQUESTING_USER';

  const getComposeDescription = () => {
    if (isSentAsRequestingUser) {
      return (
        <>
          Compose a message to send to {selectedCount} selected service
          request(s). The message will be sent as a thread message from you.
        </>
      );
    }
    if (target.kind === 'serviceRequests') return undefined;
    return (
      <>
        Compose a message to send to {selectedCount} selected
        company(s). The message will be sent into each company&apos;s general
        thread from its assigned Account Manager.
      </>
    );
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case 'compose':
        return (
          <ComposeStep
            message={message}
            setMessage={setMessage}
            selectedCount={selectedCount}
            onCancel={handleClose}
            onReview={() => setCurrentStep('review')}
            description={getComposeDescription()}
          />
        );
      case 'review':
        return target.kind === 'serviceRequests' ? (
          <ReviewStep
            selectedServiceRequests={target.items}
            isSentAsRequestingUser={isSentAsRequestingUser}
            confirmText={confirmText}
            setConfirmText={setConfirmText}
            isSending={isSending.value}
            onBack={() => {
              setConfirmText('');
              setCurrentStep('compose');
            }}
            onSend={handleSend}
          />
        ) : (
          <CompanyReviewStep
            selectedCompanies={target.items}
            confirmText={confirmText}
            setConfirmText={setConfirmText}
            isSending={isSending.value}
            onBack={() => {
              setConfirmText('');
              setCurrentStep('compose');
            }}
            onSend={handleSend}
          />
        );
      case 'processing':
        return (
          <ProcessingStep
            processedRows={processedRows}
            totalRows={totalRows}
            progressPercent={progressPercent}
            onClose={handleClose}
          />
        );
      case 'success':
        return (
          <SuccessStep
            successCount={jobStatus?.job.successCount ?? totalRows}
            totalRows={totalRows}
            onClose={handleClose}
          />
        );
      case 'failed':
        return target.kind === 'serviceRequests' ? (
          <FailedStep
            failedResults={failedSrResults}
            selectedServiceRequests={target.items}
            message={message}
            jobStatus={jobStatus}
            onClose={handleClose}
          />
        ) : (
          <CompanyFailedStep
            failedResults={failedCompanyResults}
            selectedCompanies={target.items}
            message={message}
            jobStatus={jobStatus}
            onClose={handleClose}
          />
        );
      default:
        return null;
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent
        className="max-w-[48rem] flex flex-col"
        hideCloseIcon={currentStep === 'failed' || currentStep === 'processing'}
      >
        {renderStepContent()}
      </DialogContent>
    </Dialog>
  );
};
